import React, { useState } from "react";
import { Link } from "react-router-dom";
import { auth } from "../lib/api";
import { toast } from "sonner";
import { Mail, ArrowLeft, Check } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await auth.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Couldn't send that just now. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAF7] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          data-testid="forgot-back-to-login"
          className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[#2E4531] hover:text-[#1F2F22] rounded-full pl-1.5 pr-3 py-1.5 hover:bg-[#E1ECDD] transition mb-4"
        >
          <ArrowLeft size={15} strokeWidth={2.4} /> Back to sign in
        </Link>

        <div className="bg-white rounded-3xl border border-[#DCE5DB] shadow-[0_4px_14px_rgba(0,0,0,0.05)] p-7 sm:p-8 fade-in-up">
          <div className="flex items-center gap-2.5 mb-2">
            <span className="w-9 h-9 rounded-2xl bg-[#E1ECDD] text-[#4F6F52] flex items-center justify-center">
              <Mail size={17} strokeWidth={1.8} />
            </span>
            <h1 className="font-display text-2xl text-[#2E2E2E]">Reset your password</h1>
          </div>
          <p className="text-sm text-[#4E5A4C] leading-relaxed mb-6">
            Enter the email on your LifeGuide account. We'll send you a secure link to choose a new password.
          </p>

          {sent ? (
            <div
              data-testid="forgot-sent-state"
              className="rounded-2xl border border-[#B7CCB1] bg-[#EEF3EC] p-5"
            >
              <div className="flex items-center gap-2 mb-1.5 text-[#3F5A42]">
                <Check size={16} strokeWidth={2.4} />
                <span className="font-semibold">Check your inbox</span>
              </div>
              <p className="text-[14.5px] text-[#2E2E2E] leading-relaxed">
                If an account exists for that email, we've sent password-reset instructions.
              </p>
              <p className="text-xs text-[#4E5A4C] mt-2 leading-relaxed">
                The link expires in 60 minutes. Didn't receive it? Check spam, or{" "}
                <button
                  onClick={() => setSent(false)}
                  className="underline font-semibold text-[#3F5A42] hover:text-[#2E4531]"
                >
                  try again
                </button>
                .
              </p>
              <Link
                to="/login"
                className="mt-5 block w-full text-center bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full py-3 font-semibold transition"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <label className="block">
                <span className="text-sm text-[#6B6B6B] mb-1.5 block">Email</span>
                <input
                  type="email"
                  required
                  autoFocus
                  data-testid="forgot-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3.5 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
                />
              </label>
              <button
                type="submit"
                disabled={busy || !email.trim()}
                data-testid="forgot-submit"
                className="w-full bg-[#4F6F52] hover:bg-[#3F5A42] disabled:opacity-60 text-white rounded-full py-3.5 font-semibold transition shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
              >
                {busy ? "Sending..." : "Send reset instructions"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
